/* ============================================================================
   EUPHORIUM CART HOTFIX — assets/euphorium-cart-fix.js  (revision 11)
   ----------------------------------------------------------------------------
   Surgical fix for the wrong-item / dead-button / race / stuck-spinner bugs
   in Ella's cart. Full diagnosis: CART-BUG-DIAGNOSIS.md in the project
   folder.

   Nothing in Ella is edited on disk. This file reassigns functions in memory
   after the theme loads. Delete this file + its lines in theme.liquid to
   revert.

   The fixes:
     1. Cart mutations address lines by their STABLE KEY (item.key) instead
        of their positional index. Positions shift the moment the cart
        changes; keys never do. Kills "pressed minus on B, item C changed".
     2. Mutations run through a queue, one at a time, in tap order.
     3. The unguarded policy-array lookup (global.js:549) that crashed the
        click handler is defaulted to {} before the handler runs.
     4. Drawer re-renders are serialised: while one is in flight, further
        requests collapse into a single trailing refresh with fresh data.
     6. (rev 3, narrowed in rev 5) Raw-error toast suppression. The
        add-to-cart chain's stock pre-check (theme.js:4436) shows customers
        raw exceptions like "SyntaxError: Unexpected token" when /cart.js
        returns non-JSON (throttling, network blips, app fetch interception
        — Pareto Quantity Breaks wraps cart requests). Raw exceptions become
        plain language plus a silent drawer resync; Shopify.onError's eval()
        of raw response text is replaced with safe parsing.
        IMPORTANT (rev 5): revs 3-4 also wrapped window.fetch to retry and
        synthesise /cart.js responses. ROLLED BACK — the content-type
        heuristic misjudged Pareto's reconstructed (healthy) responses as
        garbled, adding retry delays and feeding a synthetic empty cart to
        every fetch consumer, including Pareto itself (its discount badges
        vanished store-wide). Do NOT reintroduce fetch wrapping here; the
        warn-guard translation plus XHR-based resyncs cover the symptom
        without touching the transport other code depends on.
     5. (rev 2) Stuck-spinner recovery. Ella's refresh error handlers call
        $.parseJSON(xhr.responseText) blind; a network failure delivers
        HTML/empty text, parseJSON THROWS inside jQuery's callback chain,
        which also kills the complete/ajaxComplete callbacks — so the
        loading overlay stays up forever and swallows every later tap.
        Three-part fix:
          a. $.parseJSON is made non-throwing (returns a friendly fallback).
          b. Failed refresh requests get their overlays cleaned up; the
             drawer retries once with fresh data, the cart page falls back
             to one clean reload (the state is already correct server-side —
             a reload is exactly the manual fix users were performing).
          c. Removing the LAST item on the cart page reloads instead of
             running Ella's fragile empty-cart DOM surgery.
     7. (rev 10) The drawer half of 5c, plus two things it exposed. Joel's
        29 Aug report: "not when I click fast — when I go down to zero it
        gets stuck at a loading symbol; the item IS removed; then re-adding
        that product gives the syntax error."
          a. DRAWER empty-cart guard — DEFENSIVE, not a confirmed fix. 5c
             protected halo.updateCart (the cart PAGE) but not
             halo.updateSidebarCart (the DRAWER). IF the drawer render throws,
             the exception escapes, the overlay is never cleared and
             renderBusy stays latched true, so every later render is dropped.
             Now contained, overlay cleared, one-shot reload.
             HONESTY NOTE (31 Aug): this was written believing it was the
             cause of Joel's hang. It was NOT reproduced. On a live preview,
             removing the last drawer item on rev 9 emptied the cart cleanly
             with no stranded overlay and no console error — Ella's drawer
             render did not throw. The tests in tests/ exercise a mechanism
             that was authored, not observed. Keep the guard (it is free and
             the failure mode is real if it ever occurs), but do NOT claim it
             fixes the reported hang.
          b. Theme callbacks are invoked inside try/catch. They are the only
             foreign code this file calls, and (a) proved they can throw.
          c. The mutation chain can no longer be poisoned. `chain` is one
             module-level promise reused for the page's life; a single
             rejected link left it permanently rejected, and every later
             .then() passed the rejection through WITHOUT running its
             callback — every subsequent +/- and remove dead until refresh.
             It now settles fulfilled on every path.
          d. getCartJson dedupes concurrent reads and no longer retries on a
             second transport after a 429. The retry could not succeed (a
             Cloudflare challenge is not a transport fault) and added another
             request to the score that caused the challenge — failures
             generated load, which is why it never settled by itself.
        Regression tests: tests/cart-fix.test.js. Rev 9 scores 2/6, rev 10
        scores 6/6. Run it against any candidate before pasting.
     8. (rev 11) SPEND FEWER REQUESTS. This is the fix that targets the
        measured problem rather than a theorised one. Measured 31 Aug on the
        live domain: the first 7 cart requests in quick succession return
        200, and from the 8th every response is 429 with
        `cf-mitigated: challenge`. A product page load spends 4-5 cart
        requests and one add-to-cart spends 6, so ordinary shopping crosses
        the threshold — this is a volume problem, not a cart-size problem.
          a. Boot no longer reads /cart.js. The count is seeded from the
             header element the theme already renders from Liquid. Saves one
             request on EVERY page view.
          b. A 250ms read window collapses the burst of near-simultaneous
             readers around a single interaction. Every mutation clears it —
             changeByKey, any /cart/(add|change|update|clear) seen by
             ajaxComplete, and capture-phase listeners on the add-to-cart and
             quantity gestures (Ella adds via fetch, which ajaxComplete
             cannot see and which must never be wrapped).
        SCOPE: ours is only ~1 of 4-5 requests on page load and 2 of 6 on
        add-to-cart. Stack traces show the rest are Pareto Quantity Breaks
        (2 per page load, fired the same millisecond), Polar Analytics, and
        Toasti Bar. This file cannot fix those; see the 31 Aug changelog.

   Load order matters: theme.js and global.js load with defer, so this file
   must also load with defer, placed after them in the document — plus a
   retry loop in case anything shifts.
   ========================================================================== */
(function () {
  'use strict';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Indirection so automated tests can stub the reload; production behaviour
     is a plain location.reload(). */
  var hardReload = function () { window.location.reload(); };

  function warn(msg) {
    if (window.halo && typeof window.halo.showWarning === 'function') {
      window.halo.showWarning(msg);
    } else if (typeof window.showWarning === 'function') {
      window.showWarning(msg, window.warningTime || 5000);
    } else {
      console.warn('[cart-fix]', msg);
    }
  }

  /* ---- fix 2: one mutation at a time, in tap order ----------------------- */
  var chain = Promise.resolve();

  /* ---- fix 1: address lines by key, not position ------------------------- */
  /* (rev 5) The callback now fires on EVERY outcome. Previously a response
     whose body couldn't be parsed (apps like Pareto reconstruct cart
     responses when injecting discounts, and mobile networks mangle bodies)
     was logged and dropped — so the drawer never refreshed and the UI froze
     on stale state ("minus to 0 but the item never left"). Now any abnormal
     outcome resyncs from the server's real state via XHR and still calls
     back, exactly once. */
  function changeByKey(key, quantity, callback) {
    invalidateCartCache();   /* (rev 11) this mutation makes any cached cart stale */
    var run = function () {
      /* (rev 10) The theme's callback is the single most dangerous thing we
         call. Ella's drawer callback runs empty-cart DOM surgery that THROWS
         when the last line goes, and that throw used to escape into our
         promise chain — see the chain-poisoning note on `chain` above.
         Containing it here fixes both symptoms at once: the chain survives,
         and the spinner overlay gets cleared instead of stranded. */
      var finish = function (cart) {
        if (typeof callback !== 'function') return;
        try {
          callback(cart);
        } catch (e) {
          console.error('[cart-fix] theme cart callback threw; contained', e);
          try { clearDrawerOverlay(); } catch (e2) {}
        }
      };
      return fetch(root + 'cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: String(key), quantity: quantity })
      })
      .then(function (r) {
        return r.json().then(
          function (j) { return { ok: r.ok, j: j }; },
          function ()  { return { ok: r.ok, j: null }; }   /* unparseable body */
        );
      })
      .then(function (res) {
        if (res.ok && res.j && !res.j.status) { noteCount(res.j); finish(res.j); return; }
        /* e.g. stock limit hit — warn with the server's wording if we have it */
        if (res.j && (res.j.description || res.j.message)) {
          warn(res.j.description || res.j.message);
        }
        /* error status or mangled body: resync to the server's real state */
        return getCartJson().then(finish, function () {});
      })
      .catch(function (e) {
        console.error('[cart-fix]', e);
        return getCartJson().then(finish, function () {});
      });
    };

    /* (rev 10) `chain` is a single module-level promise reused for the life of
       the page. Before this, ONE rejected link left it permanently rejected —
       every later .then() passed the rejection straight through without ever
       running its callback, so every subsequent +/- and remove silently did
       nothing until the customer refreshed. Running `run` on BOTH settle paths
       and swallowing the tail guarantees the chain always settles fulfilled,
       so the queue can never be killed by one bad mutation. */
    chain = chain.then(run, run).catch(function (e) {
      console.error('[cart-fix] mutation chain recovered from', e);
    });
    return chain;
  }

  function installMutationOverride() {
    /* window.getCartUpdate is global.js's original transport — kept as the
       fallback for any caller that doesn't pass a line key. */
    if (!window.Shopify || typeof window.getCartUpdate !== 'function') return false;

    var originalTransport = window.getCartUpdate;

    /* Ella's three call sites (theme.js:4905, 5094, 5294) all pass the line
       key as the first argument — the original functions threw it away and
       used the positional index instead. Line keys always contain ':'. */
    window.Shopify.changeItem = function (lineKey, quantity, index, callback) {
      if (lineKey && String(lineKey).indexOf(':') !== -1) {
        return changeByKey(lineKey, quantity, callback);
      }
      return originalTransport(index, quantity, callback);
    };
    window.Shopify.removeItem = function (lineKey, index, callback) {
      if (lineKey && String(lineKey).indexOf(':') !== -1) {
        return changeByKey(lineKey, 0, callback);
      }
      return originalTransport(index, 0, callback);
    };

    /* (rev 6) Shopify.getCart is $.getJSON with NO fail handler
       (global.js:283) — when the read dies (mobile networks, app XHR
       interception) it fails in total silence, and since the theme's
       post-add callback is what OPENS the drawer, a successful add showed
       nothing at all. Replaced with a dual-transport read plus one delayed
       retry; the callback always gets its cart. */
    if (typeof window.Shopify.getCart === 'function' && !window.Shopify.getCart.__euWrapped) {
      var getCartReplacement = function (callback) {
        var deliver = function (cart) {
          noteCount(cart);
          if (typeof callback === 'function') callback(cart);
          else if (typeof window.Shopify.onCartUpdate === 'function') window.Shopify.onCartUpdate(cart);
        };
        getCartJson().then(deliver, function () {
          setTimeout(function () {
            getCartJson().then(deliver, function (e) {
              console.warn('[cart-fix] getCart failed twice', e);
            });
          }, 500);
        });
      };
      getCartReplacement.__euWrapped = true;
      window.Shopify.getCart = getCartReplacement;
    }
    return true;
  }

  /* ---- fix 3: crash guard for the policy-array lookup -------------------- */
  /* Capture phase runs before the button's own handler, so the array is
     guaranteed to exist by the time global.js reads it. */
  document.addEventListener('click', function (e) {
    var host = e.target && e.target.closest ? e.target.closest('cart-update-quantity') : null;
    if (host && host.dataset.product) {
      var k = 'cart_selling_array_' + host.dataset.product;
      if (window[k] == null) window[k] = {};
    }
  }, true);

  /* (rev 11) Ella's add-to-cart posts with fetch. ajaxComplete cannot see it,
     and wrapping window.fetch is permanently off the table (it broke Pareto
     store-wide in revs 3-4). So invalidate on the GESTURE instead: these run
     in the capture phase, before the theme's own handler, which guarantees
     the read window is already clear by the time the add lands. Costs one
     comparison per click and never touches the transport. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (t && t.closest &&
        t.closest('.product-form__submit, [name="add"], .btn-addToCart, .previewCartItem-remove, .btn-quantity')) {
      invalidateCartCache();
    }
  }, true);
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && f.matches && f.matches('form[action*="/cart/add"]')) invalidateCartCache();
  }, true);

  /* ---- fix 5a: non-throwing $.parseJSON ---------------------------------- */
  /* Ella's ajax error handlers do $.parseJSON(xhr.responseText).description.
     A failed request has HTML or empty responseText; parseJSON throws, and
     an exception inside jQuery's fail-callback list prevents the complete
     and ajaxComplete callbacks from ever running — which is what left the
     spinner up permanently. Valid JSON parses exactly as before. */
  function installParseGuard() {
    if (!window.jQuery || typeof window.jQuery.parseJSON !== 'function') return false;
    if (window.jQuery.parseJSON.__euSafe) return true;
    var origParse = window.jQuery.parseJSON;
    window.jQuery.parseJSON = function (s) {
      try { return origParse.call(window.jQuery, s); }
      catch (e) { return { description: 'Something went wrong. Please try again.' }; }
    };
    window.jQuery.parseJSON.__euSafe = true;
    return true;
  }

  /* ---- fix 6 (rev 3): raw-error toasts & fragile add-to-cart plumbing ----
     Observed on mobile: adding to cart showed a raw "SyntaxError: Unexpected
     token" toast while the add itself succeeded. Cause: theme.js's
     add-to-cart chain pre-checks stock with fetch('/cart.js'); when that
     response isn't JSON (throttle page, network blip, or an app's fetch
     interception — Pareto Quantity Breaks wraps cart requests), the chain
     throws and its catch displays the raw exception to the customer
     (theme.js:4492). Three layers of defence: */

  /* 6b. Never show a customer a raw JavaScript exception. If a warning is
         an Error object (or reads like one), swap it for plain language and
         quietly resync the drawer — in every observed case the operation
         itself had already succeeded server-side. Real human-written
         messages ("You can only add 2 more…") pass through untouched.

         (rev 7) The resync now also OPENS the drawer when it discovers the
         item count went UP. Observed on mobile: the add-to-cart chain dies
         at its stock pre-check, the add itself still lands (a parallel app
         flow or the in-flight POST), but the theme step that would have
         opened the drawer never runs — so customers saw nothing at all.
         Count tracking makes the recovery indistinguishable from a normal
         add: drawer slides out with the new item in it. */
  var RAW_ERROR_RE = /^(Syntax|Type|Reference|Range)Error\b/;
  function friendlyWrap(orig, ctx) {
    var wrapped = function (msg, time) {
      var raw = (msg instanceof Error) || (typeof msg === 'string' && RAW_ERROR_RE.test(msg));
      if (raw) {
        console.warn('[cart-fix] suppressed raw error toast:', msg);
        resyncAndMaybeOpen();
        return orig.call(ctx || this, 'Updating your cart…', time);
      }
      return orig.call(ctx || this, msg, time);
    };
    wrapped.__euWrapped = true;
    return wrapped;
  }

  /* last item_count seen in any cart JSON that passes through this file —
     baselined at boot so the very first recovery can detect an increase */
  var lastCount = null;
  function noteCount(cart) {
    if (cart && typeof cart.item_count === 'number') lastCount = cart.item_count;
    /* (rev 10) Every cart object this file observes is broadcast on the
       window. Widgets that want live cart state can listen instead of
       polling /cart.js — which is how the free-delivery bar updates for
       ZERO extra requests. Deliberately fire-and-forget: no listener, no
       cost, and a throwing listener must never break a cart mutation. */
    if (cart && typeof cart.total_price === 'number') {
      try {
        window.dispatchEvent(new CustomEvent('eu:cart', { detail: cart }));
      } catch (e) { /* no CustomEvent constructor: widget falls back to its own hook */ }
    }
  }
  function resyncAndMaybeOpen() {
    getCartJson().then(function (cart) {
      var prev = lastCount;
      noteCount(cart);
      var grew = prev !== null && cart.item_count > prev;
      var onCartPage = document.body.classList.contains('template-cart');
      if (grew && !onCartPage && window.jQuery) {
        window.jQuery('body').addClass('cart-sidebar-show');   /* Ella's own open mechanism */
      }
      window.halo.updateSidebarCart(cart);
    }).catch(function () {});
  }
  function installWarnGuard() {
    var done = false;
    if (window.halo && typeof window.halo.showWarning === 'function' && !window.halo.showWarning.__euWrapped) {
      window.halo.showWarning = friendlyWrap(window.halo.showWarning, window.halo);
    }
    if (typeof window.showWarning === 'function' && !window.showWarning.__euWrapped) {
      window.showWarning = friendlyWrap(window.showWarning, window);
    }
    done = !!(window.halo && window.halo.showWarning && window.halo.showWarning.__euWrapped);
    return done;
  }

  /* 6c. Shopify.onError (global.js:377) runs eval() on raw response text —
         HTML in, uncaught SyntaxError out. Same behaviour, safe parsing
         (jQuery.parseJSON is already non-throwing via fix 5a). */
  function installOnErrorGuard() {
    if (!window.Shopify || typeof window.Shopify.onError !== 'function' || !window.jQuery) return false;
    if (window.Shopify.onError.__euWrapped) return true;
    var replacement = function (xhr, textStatus, message) {
      var data = window.jQuery.parseJSON((xhr && xhr.responseText) || '') || {};
      if (data.message) {
        data.description
          ? warn(data.description)
          : warn(data.message + ': ' + message);
      } else {
        warn(data.description || ('Error : ' + message));
      }
      /* (rev 8) failed quick-adds sometimes DID land server-side (timeout
         after success, mangled response). True-up the UI either way — and
         if the count grew, open the drawer like a normal add. */
      resyncAndMaybeOpen();
    };
    replacement.__euWrapped = true;
    window.Shopify.onError = replacement;
    return true;
  }

  /* ---- fix 4 + 5b: serialise drawer re-renders, recover from failures ---- */
  var renderBusy = false, renderQueued = false, renderTimer = null;
  var drawerRetried = false;
  /* (rev 10) one-shot: stops an empty-cart render failure looping reloads */
  var emptyDrawerRecovered = false;

  /* (rev 4) Read the cart over XHR when jQuery is available, fetch only as
     a fallback. Two reasons: quantity-breaks apps wrap window.fetch (XHR
     dodges the interception entirely), and it guarantees the drawer can
     never be fed the fetch guard's synthetic empty cart. */
  /* (rev 10) Concurrent callers share one request. Several bits of this file
     plus the theme can all want the cart within the same tick; previously each
     one opened its own. No time-based caching — a stale cart is worse than an
     extra request, so the dedupe only ever covers reads that are genuinely
     in flight together. */
  var cartRead = null;

  /* (rev 11) Plus a very short result window. Measured 31 Aug: one
     add-to-cart spends 6 cart requests, two of them ours, fired within a few
     hundred ms of each other as the post-add resync and the drawer refresh
     both ask for the same cart. 250ms is long enough to collapse that burst
     and far too short to span a user action. Any mutation clears it
     immediately, so a caller can never be handed a cart from before a change
     it just made — correctness first, the request saving second.

     Window size is MEASURED, not guessed (31 Aug, live preview): on one
     add-to-cart our two reads land 1138ms apart by start time, which is
     790ms apart measured from when the first response arrived. The age is
     counted from response arrival, so 1000ms collapses them with room to
     spare. Nothing mutates the cart between those two reads — the add has
     already completed — so the cached value is not merely fresh enough, it
     is correct. */
  var CART_WINDOW_MS = 1000;
  var cartCache = null, cartCacheAt = 0;

  function invalidateCartCache() { cartCache = null; cartCacheAt = 0; }

  function getCartJson() {
    if (cartCache && (Date.now() - cartCacheAt) < CART_WINDOW_MS) {
      return Promise.resolve(cartCache);
    }
    if (cartRead) return cartRead;

    cartRead = new Promise(function (resolve, reject) {
      var viaFetch = function () {
        fetch(root + 'cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(resolve, reject);
      };
      if (window.jQuery && typeof window.jQuery.getJSON === 'function') {
        /* (rev 6) transport redundancy: XHR first (dodges app fetch
           wrappers), plain fetch as the fallback if XHR dies */
        window.jQuery.getJSON(root + 'cart.js')
          .done(resolve)
          .fail(function (xhr) {
            /* (rev 10) 429 is Cloudflare's bot challenge, not a transport
               fault. Retrying on a second transport cannot succeed and adds
               another request to the very score that produced the 429 — the
               amplification loop. Back off and let the caller handle it. */
            var status = xhr && xhr.status;
            if (status === 429 || status === 430) {
              reject(new Error('cart read challenged (' + status + ')'));
              return;
            }
            viaFetch();
          });
      } else {
        viaFetch();
      }
    });

    /* release the slot however it settles, without altering what callers see */
    cartRead.then(function (cart) {
      cartRead = null;
      if (cart && typeof cart.item_count === 'number') {
        cartCache = cart; cartCacheAt = Date.now();
      }
    }, function () {
      cartRead = null;
      invalidateCartCache();   /* never cache a failure */
    });
    return cartRead;
  }

  function refreshDrawer() {
    getCartJson()
      .then(function (cart) { noteCount(cart); window.halo.updateSidebarCart(cart); })
      .catch(function () {});
  }

  /* (rev 9) Relocate each drawer item's ✕ remove button into the
     price/quantity row, right after the price. Ella renders the ✕ as a
     direct child of the <li> and positions it absolutely — which can never
     track the price's width (sale items show TWO prices). Moving it into
     the flex row lets it sit flush beside the price at any width. The CSS
     hides not-yet-moved buttons, so there's no flash. Runs at boot and
     after every drawer render. */
  function relocateRemoveButtons() {
    var items = document.querySelectorAll('#halo-cart-sidebar .previewCartItem');
    Array.prototype.forEach.call(items, function (li) {
      var rm = li.querySelector('.previewCartItem-remove');
      var row = li.querySelector('.previewCartItem-change');
      if (!rm || !row || rm.parentElement === row) return;
      var price = row.querySelector('.previewCartItem-price');
      if (price && price.nextSibling) row.insertBefore(rm, price.nextSibling);
      else row.appendChild(rm);
    });
  }

  /* Remove the spinner overlay Ella's success handler would have removed —
     it only does so on success, never on failure. */
  function clearDrawerOverlay() {
    window.jQuery('#halo-cart-sidebar .halo-sidebar-wrapper .previewCart-wrapper')
      .removeClass('is-loading')
      .find('.loading-overlay--custom').remove();
  }
  function clearCartPageOverlay() {
    window.jQuery('[data-cart]')
      .removeClass('is-loading')
      .find('.loading-overlay').remove();
  }

  function installRenderGuard() {
    if (!window.halo || typeof window.halo.updateSidebarCart !== 'function' || !window.jQuery) return false;

    var origUpdate = window.halo.updateSidebarCart;

    window.halo.updateSidebarCart = function (cart) {
      if (renderBusy) { renderQueued = true; return; }
      renderBusy = true;
      clearTimeout(renderTimer);
      /* safety valve: never let a lost response wedge the drawer shut */
      renderTimer = setTimeout(function () { renderBusy = false; }, 10000);
      /* (rev 10) fix 5c guards Ella's empty-cart DOM surgery on the CART PAGE
         (halo.updateCart) but the DRAWER had no equivalent — and the drawer is
         where customers actually remove things. When the surgery throws, the
         exception escaped, the spinner overlay was never cleared, and the
         drawer hung until a refresh. Contain it and recover. */
      try {
        origUpdate.call(window.halo, cart);
      } catch (e) {
        console.error('[cart-fix] drawer render threw; recovering', e);
        clearTimeout(renderTimer);
        renderBusy = false;
        renderQueued = false;
        clearDrawerOverlay();
        /* An empty cart is the known-fragile case and there is nothing left to
           lose on screen, so fall back to the same deterministic reload the
           cart page already uses. Guarded so it can only ever happen once. */
        if (cart && cart.item_count === 0 && !emptyDrawerRecovered) {
          emptyDrawerRecovered = true;
          hardReload();
        }
      }
    };

    /* fix 5c: removing the last item on the cart page runs Ella's fragile
       empty-cart DOM surgery inside updateCart. One clean reload renders
       the theme's real empty-cart template instead — deterministic, and
       identical to the manual refresh users were already forced to do. */
    if (typeof window.halo.updateCart === 'function' && !window.halo.updateCart.__euWrapped) {
      var origUpdateCart = window.halo.updateCart;
      window.halo.updateCart = function (cart) {
        if (cart && cart.item_count === 0) { hardReload(); return; }
        return origUpdateCart.call(window.halo, cart);
      };
      window.halo.updateCart.__euWrapped = true;
    }

    /* Ella's refreshes use jQuery $.ajax, so their completion (success OR
       error — errors included now that fix 5a stops the handler crashing)
       is observable here without touching the original functions. */
    window.jQuery(document).ajaxComplete(function (ev, xhr, settings) {
      var url = settings && settings.url ? String(settings.url) : '';
      var failed = !xhr || xhr.status === 0 || xhr.status >= 400;

      /* (rev 11) Anything that WRITES to the cart invalidates the read window,
         whoever fired it — the theme, Pareto, Polar Analytics. Cheap
         insurance: the cost of an unnecessary invalidation is one request,
         the cost of a missed one is showing a customer a stale cart. */
      if (/\/cart\/(add|change|update|clear)/.test(url)) invalidateCartCache();

      /* -- drawer refresh -- */
      if (url.indexOf('view=ajax_side_cart') !== -1) {
        clearTimeout(renderTimer);
        renderBusy = false;
        if (failed) {
          clearDrawerOverlay();
          renderQueued = false;
          if (!drawerRetried) {          /* one retry with fresh data */
            drawerRetried = true;
            refreshDrawer();
          }
          return;
        }
        drawerRetried = false;
        relocateRemoveButtons();         /* rev 9: re-seat the ✕ after every render */
        if (renderQueued) {
          renderQueued = false;
          refreshDrawer();   /* trailing refresh with fresh data: last tap wins */
        }
        return;
      }

      /* -- cart page section refresh -- */
      if (/\/cart\?section_id=/.test(url) && failed) {
        /* The mutation already succeeded server-side; only the page refresh
           failed, leaving stale DOM under a stuck spinner. Reload delivers
           the correct state — the exact manual fix users were performing. */
        clearCartPageOverlay();
        hardReload();
      }
    });
    return true;
  }

  /* ---- boot -------------------------------------------------------------- */
  function boot() {
    var m = installMutationOverride();
    var r = installRenderGuard();
    var p = installParseGuard();
    var w = installWarnGuard();
    var o = installOnErrorGuard();
    /* Baseline the count so the first recovery can detect an increase.
       (rev 11) This used to spend a /cart.js request on EVERY page load. It
       does not need to: the theme already renders the count into the header
       from Liquid, so read it off the DOM for free. Measured 31 Aug —
       Cloudflare starts challenging at the 8th cart request in quick
       succession, and a page load plus one add-to-cart already spends about
       ten, so a request avoided on every page view is worth having.
       Falls back to the network read only if the element is missing. */
    if (lastCount === null) {
      var bubble = document.querySelector('.cartCount, [data-cart-count], .cart-count-bubble');
      var seeded = bubble ? parseInt((bubble.textContent || '').trim(), 10) : NaN;
      if (!isNaN(seeded)) lastCount = seeded;
      else getCartJson().then(noteCount).catch(function () {});
    }
    relocateRemoveButtons();   /* rev 9: seat the ✕ in the server-rendered drawer */
    if (m && r && p && w && o) return;
    var tries = 0;
    var iv = setInterval(function () {
      if (!m) m = installMutationOverride();
      if (!r) r = installRenderGuard();
      if (!p) p = installParseGuard();
      if (!w) w = installWarnGuard();
      if (!o) o = installOnErrorGuard();
      if ((m && r && p && w && o) || ++tries > 50) clearInterval(iv);
    }, 100);
  }

  /* test hook: lets the harness stub the reload without touching location */
  window.__euCartFix = {
    setReload: function (fn) { hardReload = fn; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
